import React, { useEffect, useState } from 'react';
import { Form, Input, Modal, Spin, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { postDataWithAuthToken } from '../../utils/axiosRequest';
import { alertMessage } from '../../utils/alertMessage';
import { WxWorkChatUserBindingData } from '../../types';
import UsersDropdown from '../users/common/UsersDropdown';

type WxWorkChatUserModalProps = {
  visible: boolean;
  setVisible: React.Dispatch<React.SetStateAction<boolean>>;
  /** 编辑场景: 传入当前绑定; 新增场景: undefined */
  binding?: WxWorkChatUserBindingData;
  /** 用户详情面板调起时锁定 userId, 隐藏用户选择字段 */
  lockedUserId?: number;
  callBack?: () => void;
};

/**
 * 客户群 ↔ 商城用户 绑定 Modal (新增/编辑)
 *
 * 多对多模型: 同一对 (chatId, userId) 重复提交只刷新群名/时间;
 * 不同对之间无冲突, 一个用户也可以同时在多个群。
 */
const WxWorkChatUserModal = ({
  visible,
  setVisible,
  binding,
  lockedUserId,
  callBack,
}: WxWorkChatUserModalProps) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (visible) {
      form.resetFields();
    }
  }, [visible, form]);

  const doAdd = (values: { chatId: string; userId: number }) => {
    setLoading(true);
    return postDataWithAuthToken('wxwork/chat_user/add', {
      chatId: values.chatId.trim(),
      userId: lockedUserId ?? values.userId,
    }).then((response) => {
      setLoading(false);

      if (response && response.goodStatus) {
        alertMessage(
          'success',
          binding
            ? t('wxwork.alerts.bindingEdited')
            : t('wxwork.alerts.bindingAdded')
        );
        if (callBack) callBack();
        setVisible(false);
        return;
      }

      alertMessage(
        'error',
        (response && response.msg) || t('general.noResponse'),
        (response && response.data) || undefined
      );
    });
  };

  const submitForm = () => {
    form
      .validateFields()
      .then((values) => {
        doAdd(values).catch((err) => {
          console.log(err);
          setLoading(false);
        });
      })
      .catch((err) => {
        console.log(err);
        setLoading(false);
      });
  };

  const initialValues = binding
    ? { chatId: binding.chatId, userId: binding.user?.userId }
    : lockedUserId
    ? { userId: lockedUserId }
    : {};

  return (
    <Modal
      title={
        binding
          ? `${t('wxwork.editBindingTitle')} #${binding.bindingId}`
          : t('wxwork.addBindingTitle')
      }
      visible={visible}
      onOk={submitForm}
      onCancel={() => setVisible(false)}
      okText={t('general.ok')}
      cancelText={t('general.cancel')}
      bodyStyle={{ overflow: 'auto', maxHeight: 600 }}
    >
      <Spin spinning={loading}>
        <Form form={form} layout="vertical" initialValues={initialValues}>
          <Form.Item
            name="chatId"
            label={t('wxwork.chatId')}
            extra={
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {t('wxwork.chatIdHint')}
              </Typography.Text>
            }
            rules={[
              { required: true, message: t('general.inputError.empty') },
            ]}
          >
            <Input placeholder="wrS4NtFwAAONmh2k..." />
          </Form.Item>
          {!lockedUserId && (
            <Form.Item
              name="userId"
              label={t('wxwork.user')}
              rules={[
                { required: true, message: t('general.inputError.empty') },
              ]}
            >
              <UsersDropdown
                initialValue={binding ? binding.user?.userId : undefined}
                initialUser={binding?.user}
                onChange={(value) => form.setFieldsValue({ userId: value })}
              />
            </Form.Item>
          )}
        </Form>
      </Spin>
    </Modal>
  );
};

export default WxWorkChatUserModal;
